/**
 * Screen share (Go Live) and camera in the voice room
 * (docs/decisions/2026-10-29-screen-share-and-video.md §4.7): the bar's
 * Camera and Share buttons with the share sheet, stream tiles (LIVE, the
 * viewer chip, Watch stream, focus and full screen), a camera in its
 * person's tile, and what the room tells comms-voice is on screen
 * (`visible`: tiles on screen only, a screen only while you watch it).
 * A watched stream has Stop watching (in words when focused) and its own
 * sound: a volume to 200 % and a mute, apart from the voice, remembered
 * per streamer (owner 2026-11-01), the volume on a mouse's hover too; the
 * pop-out is StreamMini.tsx.
 *
 * Glass kit and tokens only (voice.css). Names keep their own direction
 * (`dir="auto"`, `<bdi>`).
 */
import { Permissions, type Pid, type Snowflake, type VoiceVideoRef } from '@neosocial/protocol';
import { readLayout, useLayout } from '@neosocial/ui/layout';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import { tr, useT } from '../i18n';
import { listFormat } from '../i18n/core';
import { useData } from '../store/data';
import { can, channelPermissions, displayName } from '../store/selectors';
import { Avatar, Btn, gradStyle, Icon, Range, Seg, Switch } from '../ui/kit';
import {
  openMenu,
  openPopover,
  rememberAnchor,
  type LiveMenuItems,
  type MenuItem,
} from '../ui/overlay';
import {
  currentCaps,
  goLive,
  mediaFlagOn,
  remoteStream,
  setLiveQuality,
  setStreamMuted,
  setStreamVolume,
  setVisibleVideos,
  stopMedia,
  stopWatching,
  streamSoundOf,
  toggleCamera,
  tokenStreamAllows,
  useLocalMedia,
  watchStream,
} from '../voice/live';
import { MAX_STREAM_VOLUME } from '../voice/types';
import { forgetStreamFocus, setStageOn, type FocusKey, type TileTap } from './streamView';
import {
  cameraSupported,
  canRestrictOwnAudio,
  defaultPick,
  displayCaptureSupported,
  heightChoices,
  rateChoices,
  refKey,
  screenAudioSupported,
  visibleRefs,
  type ScreenPick,
} from '../voice/media';
import { useVoice } from '../voice/store';
import type { RosterMember } from '../voice/types';
import { useConfig } from '../store/config';
import './streamUi';

/* ------------------------------ who shows what ------------------------------ */

/** A live screen in the room: its streamer's pid and user, and whether it carries sound. */
export interface RoomStream {
  pid: Pid;
  userId: Snowflake;
  audio: boolean;
  me: boolean;
}

/** The roster's people in `channelId` with a live screen, yours first (Discord's order: streams first). */
export function roomStreams(
  members: Record<Pid, RosterMember>,
  channelId: Snowflake,
  myPid: Pid | null,
): RoomStream[] {
  return Object.values(members)
    .filter((m) => m.channel === channelId && m.screen)
    .sort((a, b) => (a.pid === myPid ? -1 : b.pid === myPid ? 1 : a.pid - b.pid))
    .map((m) => ({ pid: m.pid, userId: m.user_id, audio: !!m.screen_audio, me: m.pid === myPid }));
}

export type { FocusKey };
export const sameFocus = (a: FocusKey | null, b: FocusKey | null) =>
  !!a && !!b && a.kind === b.kind && a.userId === b.userId;

/** Your media flags as the screens follow them: the live config, else the token. */
export function useMediaFlags(): { share: boolean; camera: boolean } {
  const grant = useLocalMedia((m) => m.grant);
  const flags = useConfig(
    (c) => c.config?.flags as Record<string, boolean | undefined> | undefined,
  );
  return {
    share: mediaFlagOn('screen_share', grant, flags),
    camera: mediaFlagOn('video', grant, flags),
  };
}

/** STREAM in the channel you are in, live (roles and overwrites as they are now). */
export function useCanStream(channelId: Snowflake | null): boolean {
  // your roles and the channel's overwrites as they are now; a hidden
  // channel (no data here) and a DM call (no roles: every DM room, as
  // Discord) go by the token (tokenStreamAllows), as streamAllowedIn (QA NV-02)
  const byRoles = useData((d) => {
    const c = channelId ? d.channels[channelId] : undefined;
    return c?.guild_id ? can(channelPermissions(d, c), Permissions.STREAM) : null;
  });
  const byToken = useLocalMedia((m) => !!channelId && tokenStreamAllows(channelId, m));
  return byRoles ?? byToken;
}

/* ------------------------------ video ------------------------------ */

/** A `<video>` for a MediaStream: muted (sound plays apart), inline, rounded by its tile. */
export function VideoView({
  stream,
  fit,
  mirror,
  label,
}: {
  stream: MediaStream | null;
  fit: 'contain' | 'cover';
  mirror?: boolean;
  label?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  // muted, inline and autoplay: it plays as soon as it has a stream (iOS too)
  useEffect(() => {
    const v = ref.current;
    if (v && v.srcObject !== stream) v.srcObject = stream;
  }, [stream]);
  // a live video is never meant to stay paused: iOS pauses it when its own
  // full screen ends or the app comes back, and a browser's picture-in-
  // picture when it closes; it plays on (owner 2026-11-01, "it won't show
  // it to you anymore")
  useEffect(() => {
    const v = ref.current;
    if (!v || typeof document === 'undefined') return;
    const resume = () => {
      // a video taken off the page is paused for good (it is going away)
      if (!v.paused || !v.srcObject || !v.isConnected || document.visibilityState === 'hidden')
        return;
      try {
        void v.play()?.catch(() => {});
      } catch {
        /* an engine without play() */
      }
    };
    v.addEventListener('pause', resume);
    v.addEventListener('webkitendfullscreen', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      v.removeEventListener('pause', resume);
      v.removeEventListener('webkitendfullscreen', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, []);
  return (
    <video
      ref={ref}
      className="hubv-video"
      data-fit={fit}
      data-mirror={mirror || undefined}
      aria-label={label}
      autoPlay
      playsInline
      muted
    />
  );
}

/**
 * A watched screen's video, from the slot that carries it now: keyed by the
 * slot, so a stream that comes back on another slot (after the room was
 * away) never keeps a stale one. Null while not watched or not on a slot.
 */
export function useScreenStream(pid: Pid, watched: boolean): MediaStream | null {
  const mid = useVoice((v) => {
    if (!watched) return null;
    for (const [m, r] of Object.entries(v.session.vslots))
      if (r.pid === pid && r.source === 'screen') return m;
    return null;
  });
  return mid !== null ? remoteStream(pid, 'screen') : null;
}

/** A stream's sound as you set it (its volume 0..2 and your mute), live. */
export function useStreamSound(userId: Snowflake): { volume: number; muted: boolean } {
  const volume = useLocalMedia((m) => streamSoundOf(userId, m).volume);
  const muted = useLocalMedia((m) => streamSoundOf(userId, m).muted);
  return { volume, muted };
}

type FsDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
  webkitFullscreenEnabled?: boolean;
};

/** Can a page element go full screen here? An iPhone can't (only a video can). */
export function fullscreenSupported(): boolean {
  if (typeof document === 'undefined') return false;
  const doc = document as FsDocument;
  return !!(document.fullscreenEnabled || doc.webkitFullscreenEnabled);
}

/** The element full screen now (Safari's prefixed one too), or null. */
export function fullscreenElement(): Element | null {
  if (typeof document === 'undefined') return null;
  const doc = document as FsDocument;
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

/** Calls `fn` on every full-screen change (Safari's prefixed event too); returns the stop. */
function onFullscreenChange(fn: () => void): () => void {
  document.addEventListener('fullscreenchange', fn);
  document.addEventListener('webkitfullscreenchange', fn);
  return () => {
    document.removeEventListener('fullscreenchange', fn);
    document.removeEventListener('webkitfullscreenchange', fn);
  };
}

/**
 * The app's pop-ups (menus, sheets, tooltips, toasts, dialogs) live in
 * #nx-portal, outside whatever is full screen, and full screen shows that
 * element alone: while a room or a tile is full screen the portal moves
 * into it, so its menus still open there, and back to the page after. A
 * full-screen video on its own (iOS) has no menus.
 */
function portalFollowsFullscreen(): void {
  const portal = document.getElementById('nx-portal');
  if (!portal) return;
  const el = fullscreenElement();
  const host = el instanceof HTMLElement && !(el instanceof HTMLVideoElement) ? el : document.body;
  if (portal.parentElement !== host) host.appendChild(portal);
}
if (typeof document !== 'undefined') onFullscreenChange(portalFollowsFullscreen);

/**
 * Full screen for a tile (double-click or its button) or the room (its
 * header's button); iOS plays the video full screen. A tile in the full-
 * screen room goes full screen over it, and leaves it to the room again.
 */
export function toggleFullscreen(tile: HTMLElement): void {
  const doc = document as FsDocument;
  const cur = fullscreenElement();
  if (cur && (cur === tile || !cur.contains(tile))) {
    if (document.exitFullscreen) void document.exitFullscreen().catch(() => {});
    else doc.webkitExitFullscreen?.();
    return;
  }
  const el = tile as HTMLElement & { webkitRequestFullscreen?: () => void };
  if (el.requestFullscreen) {
    void el.requestFullscreen().catch(() => {});
    return;
  }
  if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
  const v = tile.querySelector('video') as
    (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
  v?.webkitEnterFullscreen?.();
}

/** Is a tile (or the room) full screen now (its button's label)? */
export function useFullscreen(ref: RefObject<HTMLElement | null>): boolean {
  const [on, setOn] = useState(false);
  useEffect(
    () => onFullscreenChange(() => setOn(!!ref.current && fullscreenElement() === ref.current)),
    [ref],
  );
  return on;
}

/* ------------------------------ what is on screen ------------------------------ */

/** A video tile's ref callback: it is watched while mounted (React 19 runs the cleanup). */
export type TileTracker = (el: HTMLElement | null) => (() => void) | undefined;

/**
 * What full screen shows of the stage: nothing of it (`undefined`), one of
 * its tiles (that tile's `data-vref`, or null for a tile with no video ref),
 * or something else over all of it (`false`).
 */
type StageFullscreen = undefined | string | null | false;

function stageFullscreen(stage: HTMLElement | null): StageFullscreen {
  const el = fullscreenElement();
  if (!el || !stage || el.contains(stage)) return undefined;
  if (!stage.contains(el)) return false;
  return (el as HTMLElement).dataset?.vref ?? null;
}

/**
 * Tells comms-voice which videos are on screen (§2.1 rule 3): the tiles the
 * stage shows that are not fully scrolled away (IntersectionObserver), a
 * screen only while you watch it, the focused one first; `[]` when the room
 * closes or is not on screen (`roomShown`: a phone's list over it, or its
 * chat over the whole stage). A tile in full screen is the one thing on
 * screen: the observer, rooted at the stage, takes a tile in the top layer
 * for gone (QA FS-1), so it is kept by `document.fullscreenElement`
 * instead. Returns the ref callback each video tile takes (`data-vref`).
 *
 * With `channelId` it also says whether the stage is on screen
 * (`setStageOn`, for the stream pop-out), handing a watched stream over
 * without a gap: going away, the pop-out takes it before the room lets it
 * go; coming back, the room asks for it (once its tiles are measured)
 * before the pop-out lets it go.
 */
export function useVideoVisibility(
  stage: RefObject<HTMLElement | null>,
  refs: VoiceVideoRef[],
  focus: VoiceVideoRef | null,
  roomShown = true,
  channelId: Snowflake | null = null,
): TileTracker {
  const watching = useVoice((v) => v.session.watching);
  const [shown, setShown] = useState<Record<string, boolean>>({});
  // the observer has measured the tiles once (or there is none, or it is late)
  const [measured, setMeasured] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (measured) return;
    const t = setTimeout(() => setMeasured(true), 500);
    return () => clearTimeout(t);
  }, [measured]);
  const [full, setFull] = useState<StageFullscreen>(undefined);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const read = () => setFull(stageFullscreen(stage.current));
    read();
    return onFullscreenChange(read);
  }, [stage]);
  const io = useRef<IntersectionObserver | null>(null);
  const els = useRef(new Set<HTMLElement>());
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const o = new IntersectionObserver(
      (entries) => {
        setMeasured(true);
        setShown((cur) => {
          let next = cur;
          for (const e of entries) {
            const k = (e.target as HTMLElement).dataset.vref;
            if (!k || next[k] === e.isIntersecting) continue;
            if (next === cur) next = { ...cur };
            next[k] = e.isIntersecting;
          }
          return next;
        });
      },
      { root: stage.current, threshold: 0 },
    );
    io.current = o;
    for (const el of els.current) o.observe(el);
    return () => {
      o.disconnect();
      io.current = null;
    };
  }, [stage]);
  const track = useCallback<TileTracker>((el) => {
    if (!el) return undefined;
    els.current.add(el);
    io.current?.observe(el);
    return () => {
      els.current.delete(el);
      io.current?.unobserve(el);
    };
  }, []);
  const observing = typeof IntersectionObserver !== 'undefined';
  const onScreen = (r: VoiceVideoRef) => {
    if (full !== undefined) return full === refKey(r);
    return observing ? !!shown[refKey(r)] : true;
  };
  // the list by its content: the room re-renders often (levels), the list rarely changes
  const list = JSON.stringify(
    visibleRefs({
      shown: roomShown,
      tiles: refs.map((r) => ({ ref: r, onScreen: onScreen(r) })),
      watching,
      focus,
    }),
  );
  const on = roomShown && measured;
  useEffect(() => {
    const next = JSON.parse(list) as VoiceVideoRef[];
    // coming into view: the room's list first; going away: the stage's
    // place first (the pop-out takes over), then the room's empty list
    if (on) {
      setVisibleVideos(next);
      if (channelId) setStageOn(channelId);
    } else {
      if (channelId) setStageOn(null);
      setVisibleVideos(next);
    }
  }, [list, on, channelId]);
  // the room is no longer the open pane: nothing is on screen (the pop-out first)
  useEffect(
    () => () => {
      if (channelId) setStageOn(null);
      setVisibleVideos([]);
    },
    [channelId],
  );
  return track;
}

/* ------------------------------ the bar ------------------------------ */

/** A small glass note on a button ("Coming soon", or why you can't stream here). */
export function openNote(anchor: HTMLElement, title: string, body?: string): void {
  openPopover(
    anchor,
    () => (
      <div className="hubv-note" role="status">
        <b>{title}</b>
        {body ? <span>{body}</span> : null}
      </div>
    ),
    { placement: 'top', cls: 'hubv-note-pop', label: title, focus: false },
  );
}

/** The share sheet (§4.7): resolution and frame rate up to the caps, sound where it can, Go Live. */
function ShareSheet({ close }: { close: () => void }) {
  const { t } = useT();
  const caps = currentCaps();
  const heights = heightChoices(caps.screen_height);
  const rates = rateChoices(caps.screen_fps);
  const [height, setHeight] = useState(() => defaultPick(heights, 720));
  const [fps, setFps] = useState(() => defaultPick(rates, 30));
  const soundOk = screenAudioSupported();
  // on where it cannot echo the voice chat back (Discord shares sound by default)
  const [audio, setAudio] = useState(canRestrictOwnAudio);
  const echo = audio && !canRestrictOwnAudio();
  const pick: ScreenPick = { height, fps, audio: soundOk && audio };
  return (
    <div className="hubv-sh">
      <div className="hubv-sh-h">
        <span className="hubv-sh-ic" aria-hidden="true">
          <Icon name="monitor" />
        </span>
        <b>{t('stream.sheet.title')}</b>
      </div>
      <div className="hubv-sh-row">
        <span>{t('stream.sheet.res')}</span>
        <Seg
          label={t('stream.sheet.res')}
          value={String(height)}
          onChange={(v) => setHeight(Number(v))}
          options={heights.map((h) => ({ value: String(h), label: t('stream.res', { n: h }) }))}
        />
      </div>
      <div className="hubv-sh-row">
        <span>{t('stream.sheet.fps')}</span>
        <Seg
          label={t('stream.sheet.fps')}
          value={String(fps)}
          onChange={(v) => setFps(Number(v))}
          options={rates.map((f) => ({ value: String(f), label: t('stream.fpsN', { n: f }) }))}
        />
      </div>
      {soundOk ? (
        <div className="hubv-sh-row">
          <span>{t('stream.sheet.sound')}</span>
          <Switch checked={audio} onChange={setAudio} label={t('stream.sheet.sound')} />
        </div>
      ) : null}
      {echo ? (
        <p className="hubv-sh-note">
          <Icon name="info" size="s" />
          <span>{t('stream.sheet.echo')}</span>
        </p>
      ) : null}
      <Btn
        kind="primary"
        icon="monitor"
        className="hubv-sh-go"
        data-autofocus=""
        onClick={() => {
          // the system picker opens from this click (it needs the gesture)
          void goLive(pick);
          close();
        }}
      >
        {t('stream.sheet.go')}
      </Btn>
    </div>
  );
}

export function openShareSheet(anchor: HTMLElement): void {
  rememberAnchor(anchor);
  openPopover(anchor, (close) => <ShareSheet close={close} />, {
    placement: 'top',
    cls: 'hubv-sh-pop',
    label: tr().t('stream.sheet.title'),
  });
}

/** You are streaming: a publication of your screen, or its capture still open. */
export function useStreaming(): boolean {
  const live = useVoice((v) => !!v.session.live.screen);
  const mine = useLocalMedia((m) => !!m.screen);
  return live || mine;
}

/**
 * Share for the bar and the dock; while you stream it is **Stop streaming**
 * (red, the crossed monitor, and in words where `labeled`), on every layout
 * and whatever the flags say: a way out is never hidden (live bug
 * 2026-10-30, "there is no end stream": a window narrowed to the tablet
 * layout lost it). Share itself is hidden on phones and tablets, wherever
 * `getDisplayMedia` is missing, and while `screen_share` is off for you;
 * without STREAM here it is disabled and says why.
 */
export function ShareButton({
  cls,
  channelId,
  labeled = false,
}: {
  cls: string;
  channelId: Snowflake | null;
  labeled?: boolean;
}) {
  const { t } = useT();
  const lay = useLayout();
  const flags = useMediaFlags();
  const allowed = useCanStream(channelId);
  const streaming = useStreaming();
  if (streaming)
    return (
      <button
        type="button"
        className={cls + ' on live stop'}
        aria-pressed={true}
        aria-label={t('stream.stop')}
        data-tip={labeled ? undefined : t('stream.stop')}
        data-stream=""
        onClick={() => stopMedia('screen')}
      >
        <Icon name="monitor-off" />
        {labeled ? <span className="lbl">{t('stream.stop')}</span> : null}
      </button>
    );
  if (lay.layout !== 'desktop' || !displayCaptureSupported() || !flags.share) return null;
  return (
    <button
      type="button"
      className={cls + (allowed ? '' : ' na')}
      aria-pressed={false}
      aria-disabled={allowed ? undefined : true}
      aria-haspopup="dialog"
      aria-label={allowed ? t('stream.share') : t('stream.noPerm')}
      data-tip={allowed ? t('stream.share') : t('stream.noPerm')}
      data-stream=""
      onClick={(e) =>
        allowed ? openShareSheet(e.currentTarget) : openNote(e.currentTarget, t('stream.noPerm'))
      }
    >
      <Icon name="monitor" />
    </button>
  );
}

/**
 * The dock's streaming line, under the voice row (Discord's voice panel):
 * LIVE, "Your stream", and **Stop streaming** in words.
 */
export function StreamingRow() {
  const { t } = useT();
  const streaming = useStreaming();
  if (!streaming) return null;
  return (
    <div className="nx-vlive" role="group" aria-label={t('stream.yours')}>
      <span className="nx-live-badge">{t('stream.live')}</span>
      <span className="t">{t('stream.yours')}</span>
      <Btn
        kind="danger"
        size="xs"
        icon="monitor-off"
        data-stop-stream="dock"
        onClick={() => stopMedia('screen')}
      >
        {t('stream.stop')}
      </Btn>
    </div>
  );
}

/**
 * The camera: "Coming soon" (a glass note) while `video` is off for you;
 * without STREAM here, disabled with why; else on and off.
 */
export function CameraButton({ cls, channelId }: { cls: string; channelId: Snowflake | null }) {
  const { t } = useT();
  const flags = useMediaFlags();
  const allowed = useCanStream(channelId);
  const on = useLocalMedia((m) => !!m.camera);
  if (!cameraSupported()) return null;
  if (!flags.camera)
    return (
      <button
        type="button"
        className={cls + ' na soon'}
        aria-disabled={true}
        aria-label={t('stream.err.soon')}
        data-tip={t('stream.soon')}
        data-camera="soon"
        onClick={(e) => openNote(e.currentTarget, t('stream.soon'), t('stream.soonBody'))}
      >
        <Icon name="video" />
      </button>
    );
  if (!allowed && !on)
    return (
      <button
        type="button"
        className={cls + ' na'}
        aria-disabled={true}
        aria-label={t('stream.noPerm')}
        data-tip={t('stream.noPerm')}
        data-camera="no-perm"
        onClick={(e) => openNote(e.currentTarget, t('stream.noPerm'))}
      >
        <Icon name="video" />
      </button>
    );
  return (
    <button
      type="button"
      className={cls + (on ? ' on' : '')}
      aria-pressed={on}
      aria-label={t(on ? 'stream.camOff' : 'stream.camOn')}
      data-tip={t(on ? 'stream.camOff' : 'stream.camOn')}
      data-camera={on ? 'on' : 'off'}
      onClick={() => void toggleCamera()}
    >
      <Icon name="video" />
    </button>
  );
}

/* ------------------------------ stream tiles ------------------------------ */

const VIEWER_NAMES_MAX = 10;

/** "Ana, Bahar and 3 more": at most ten names (§4.7). */
export function viewerWords(names: string[]): string {
  const { t } = tr();
  if (!names.length) return t('stream.noViewers');
  const shown = names.slice(0, VIEWER_NAMES_MAX);
  const rest = names.length - shown.length;
  return rest > 0
    ? listFormat([...shown, t('stream.viewersMore', { n: rest })])
    : listFormat(shown);
}

/**
 * The viewer chip: the count, and the names on hover or tap. On a compact
 * tile (`still`) it is the count alone: no tap target near the middle a
 * tap focuses (a finger's tap snaps to the nearest button).
 */
function ViewerChip({ pid, still }: { pid: Pid; still?: boolean }) {
  const { t, fmt } = useT();
  const viewers = useVoice((v) => v.session.viewers[pid]);
  const names = useVoice((v) =>
    (v.session.viewers[pid] ?? [])
      .map((p) => v.session.members[p]?.name)
      .filter((n): n is string => !!n)
      .join('\u0000'),
  );
  const list = names ? names.split('\u0000') : [];
  const n = viewers?.length ?? 0;
  const words = viewerWords(list);
  if (still)
    return (
      <span
        className="hubv-tile-viewers"
        role="img"
        aria-label={t('stream.viewers', { n }) + ': ' + words}
        data-tip={words}
        data-viewers={n}
      >
        <Icon name="eye" size="s" />
        <bdi className="nx-num">{fmt.num(n)}</bdi>
      </span>
    );
  return (
    <button
      type="button"
      className="hubv-tile-viewers"
      aria-label={t('stream.viewers', { n }) + ': ' + words}
      data-tip={words}
      data-viewers={n}
      onClick={(e) => {
        e.stopPropagation();
        openNote(e.currentTarget, t('stream.viewers', { n }), list.length ? words : undefined);
      }}
    >
      <Icon name="eye" size="s" />
      <bdi className="nx-num">{fmt.num(n)}</bdi>
    </button>
  );
}

/** A stream's volume in percent, as the slider and its value say it. */
const pct = (v: number) => tr().fmt.num(v / 100, { style: 'percent', maximumFractionDigits: 0 });

/** Is `pid`'s screen watched now? */
const watchedNow = (pid: Pid) => !!useVoice.getState().session.watching[pid];

/**
 * Stop watching (Discord's): `watch off` (no more of its video or sound),
 * and a focus on it goes back to the grid.
 */
export function stopWatchingStream(st: Pick<RoomStream, 'pid' | 'userId'>): void {
  forgetStreamFocus(st.userId);
  void stopWatching(st.pid);
}

/**
 * A stream's sound items, live while the menu is open: Stream volume (a
 * slider from 0 to 200 %, which never closes the menu) and Mute stream
 * sound; both apart from the person's voice, remembered per streamer.
 */
export function streamSoundItems(userId: Snowflake): MenuItem[] {
  const { t } = tr();
  const { volume, muted } = streamSoundOf(userId);
  return [
    {
      id: 'stream-volume',
      label: t('stream.volume'),
      range: {
        value: muted ? 0 : Math.round(volume * 100),
        min: 0,
        max: MAX_STREAM_VOLUME * 100,
        step: 5,
        format: pct,
        onChange: (v) => setStreamVolume(userId, v / 100),
      },
    },
    {
      id: 'stream-mute',
      label: t('stream.muteSound'),
      icon: muted ? 'volume-x' : 'volume',
      toggle: true,
      checked: muted,
      keepOpen: true,
      run: () => setStreamMuted(userId, !muted),
    },
  ];
}

/** Items that follow the stores while the menu is open (cached by `key`). */
export function liveItems(key: () => string, build: () => MenuItem[]): LiveMenuItems {
  let k: string | null = null;
  let items: MenuItem[] = [];
  return {
    subscribe: (fn) => {
      const a = useVoice.subscribe(fn);
      const b = useLocalMedia.subscribe(fn);
      return () => {
        a();
        b();
      };
    },
    get: () => {
      const now = key();
      if (now !== k) {
        k = now;
        items = build();
      }
      return items;
    },
  };
}

/** A watched stream's sound on its own: Stream volume and Mute stream sound. */
export function streamSoundMenu(anchor: HTMLElement, userId: Snowflake): void {
  rememberAnchor(anchor);
  const key = () => {
    const s = streamSoundOf(userId);
    return JSON.stringify([s.volume, s.muted]);
  };
  openMenu(
    anchor,
    liveItems(key, () => streamSoundItems(userId)),
    {
      placement: 'bottom-end',
      label: tr().t('stream.sound'),
    },
  );
}

/**
 * A watched stream's sound button (its tile and the pop-out): a click mutes
 * it (Discord's). On a mouse, hovering it (or the keyboard's focus on it)
 * shows a small glass slider under it, the stream's volume from 0 to 200 %
 * (`slider`; off where the frame has no room for it); a tap on a touch
 * screen, with no hover, opens the volume and the mute instead.
 */
export function StreamSoundButton({
  userId,
  slider = true,
  data,
}: {
  userId: Snowflake;
  slider?: boolean;
  /** The button's own data attributes (what tests and styles find it by). */
  data?: Record<string, string | undefined>;
}) {
  const { t } = useT();
  const coarse = useLayout().pointer === 'coarse';
  const sound = useStreamSound(userId);
  const value = sound.muted ? 0 : Math.round(sound.volume * 100);
  // its tip and its icon follow the mute and the volume (QA V-15); a toggle
  // keeps one name, and `aria-pressed` says muted
  const button = (
    <button
      type="button"
      className="hubv-tt"
      aria-pressed={sound.muted}
      aria-label={t('stream.muteSound')}
      data-tip={t(sound.muted ? 'stream.unmuteSound' : 'stream.muteSound')}
      {...data}
      onClick={(e) => {
        if (readLayout().pointer === 'coarse') streamSoundMenu(e.currentTarget, userId);
        else setStreamMuted(userId, !sound.muted);
      }}
    >
      <Icon name={!value ? 'volume-x' : value < 50 ? 'volume-low' : 'volume'} size="m" />
    </button>
  );
  if (!slider || coarse) return button;
  return (
    <span className="hubv-snd">
      {button}
      <span className="hubv-snd-pop">
        <span className="hubv-snd-in">
          <Range
            value={value}
            min={0}
            max={MAX_STREAM_VOLUME * 100}
            step={5}
            aria-label={t('stream.volume')}
            aria-valuetext={pct(value)}
            data-stream-volume=""
            onChange={(e) => setStreamVolume(userId, Number(e.currentTarget.value) / 100)}
          />
          <span className="hubv-snd-v nx-num" aria-hidden="true">
            {pct(value)}
          </span>
        </span>
      </span>
    </span>
  );
}

/**
 * A stream's own menu: its sound (watched, with sound), focus, watch or stop
 * watching; your quality and Stop streaming. From its ⋯ button, or at the
 * pointer from a right-click or a long-press on the tile (Discord's), which
 * also offers full screen (`tile`, while it shows video).
 */
function streamMenu(
  anchor: HTMLElement | { x: number; y: number },
  st: RoomStream,
  o: { focused: boolean; onFocus: () => void; tile?: HTMLElement | null },
): void {
  const { t } = tr();
  const atPointer = !(anchor instanceof HTMLElement);
  rememberAnchor(atPointer ? (o.tile ?? document.body) : anchor);
  const placement = atPointer ? 'bottom-start' : 'bottom-end';
  const tile = o.tile;
  const fullItem = (): MenuItem[] =>
    tile
      ? [
          {
            id: 'fullscreen',
            label: t(fullscreenElement() === tile ? 'stream.exitFullscreen' : 'stream.fullscreen'),
            icon: 'win-max',
            run: () => toggleFullscreen(tile),
          },
        ]
      : [];
  if (!st.me) {
    const key = () => {
      const s = streamSoundOf(st.userId);
      return JSON.stringify([watchedNow(st.pid), s.volume, s.muted]);
    };
    const build = (): MenuItem[] => {
      const watching = watchedNow(st.pid);
      const items: MenuItem[] = [];
      if (watching && st.audio) items.push(...streamSoundItems(st.userId), { sep: true });
      items.push({
        id: 'focus',
        label: t(o.focused ? 'stream.grid' : 'stream.focus'),
        icon: o.focused ? 'grid' : 'layers',
        run: o.onFocus,
      });
      if (watching) items.push(...fullItem());
      items.push(
        watching
          ? {
              id: 'stop-watching',
              label: t('stream.stopWatching'),
              icon: 'cm-eye-off',
              run: () => {
                if (o.focused) o.onFocus();
                stopWatchingStream(st);
              },
            }
          : {
              id: 'watch',
              label: t('stream.watch'),
              icon: 'eye',
              run: () => {
                if (!o.focused) o.onFocus();
                void watchStream(st.pid, true);
              },
            },
      );
      return items;
    };
    openMenu(anchor, liveItems(key, build), { placement, label: t('stream.menu') });
    return;
  }
  // your own stream: its quality in place, and Stop streaming
  const items: MenuItem[] = [];
  const caps = currentCaps();
  const lm = useLocalMedia.getState();
  const q = lm.quality ?? (lm.pick ? { height: lm.pick.height, fps: lm.pick.fps } : null);
  for (const h of heightChoices(caps.screen_height))
    for (const f of rateChoices(caps.screen_fps))
      items.push({
        id: `q-${h}-${f}`,
        label: `${t('stream.res', { n: h })} · ${t('stream.fpsN', { n: f })}`,
        checked: !!q && q.height === h && q.fps === f,
        run: () => setLiveQuality({ height: h, fps: f }),
      });
  items.push({ sep: true }, ...fullItem());
  items.push({
    id: 'stop-stream',
    label: t('stream.stop'),
    icon: 'x',
    danger: true,
    run: () => stopMedia('screen'),
  });
  openMenu(anchor, items, { placement, label: t('stream.quality') });
}

/**
 * A stream tile (§4.7): LIVE, the viewer chip and a sound mark; a stream
 * you don't watch shows its streamer on glass and **Watch stream** (which
 * opens the focus view); a watched one plays; yours is a local, muted
 * preview with **Stop streaming**. A click, or Enter or Space on the
 * focused tile, focuses it (`onTap`, the stage's: a double-click's second
 * click undoes it and goes full screen, wherever the re-laid-out stage put
 * the tile), and its button goes full screen.
 */
export function StreamTile({
  st,
  guildId,
  av,
  compact = false,
  focused,
  onFocus,
  onTap,
  track,
  i,
}: {
  st: RoomStream;
  guildId: Snowflake | null;
  av: number;
  /** Under 100 tall (a crowded phone, the strip): a tap or a click focuses it (and watches it), and the big tile has the rest. */
  compact?: boolean;
  focused: boolean;
  onFocus: (on: boolean) => void;
  onTap: (how?: TileTap) => void;
  track: TileTracker;
  i: number;
}) {
  const { t } = useT();
  const user = useData((d) => d.users[st.userId]);
  const name = useData((d) =>
    user ? displayName(user, guildId ? d.members[guildId]?.[st.userId] : undefined) : st.userId,
  );
  const watching = useVoice((v) => !!v.session.watching[st.pid]);
  const sending = useVoice((v) => !!v.session.live.screen?.sending);
  const preview = useLocalMedia((m) => (st.me ? m.screen : null));
  const remote = useScreenStream(st.pid, !st.me && watching);
  const stream = st.me ? preview : remote;
  // its sound as you set it (only a watched stream's plays)
  const sound = useStreamSound(st.userId);
  const soundMuted = !st.me && watching && sound.muted;
  const soundButton = !st.me && watching && st.audio && !compact;
  // a finger's tools stay shown, so there the button is the one sound mark;
  // a mouse's show only on hover, so its mark stays (QA V-15)
  const coarse = useLayout().pointer === 'coarse';
  const ref = useRef<HTMLDivElement | null>(null);
  const full = useFullscreen(ref);
  const label = st.me ? t('stream.yours') : t('stream.of', { name });
  // Stop watching in words on the focused tile, but not on a phone: its
  // ~360px tile has no room for the words beside LIVE, the viewers and the
  // sound chip (QA V-04: the tools drew over them)
  const phone = useLayout().layout === 'phone';
  const unwatchWords = focused && !phone;
  const unwatch = () => {
    // Discord goes back to the grid
    if (focused) onFocus(false);
    stopWatchingStream(st);
  };
  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      ref.current = el;
      const off = track(el);
      return () => {
        ref.current = null;
        off?.();
      };
    },
    [track],
  );
  return (
    <div
      ref={setRef}
      className={
        'hubv-tile hubv-stile' +
        (st.me ? ' me' : '') +
        (compact ? ' compact' : '') +
        (focused ? ' focused' : '') +
        (stream ? ' has-video' : '')
      }
      data-kind="stream"
      data-user={st.userId}
      data-vref={`${st.pid}:screen`}
      data-watching={watching || undefined}
      tabIndex={0}
      role="group"
      aria-label={label}
      style={{ ...gradStyle(st.userId), '--i': i } as CSSProperties}
      onClick={(e) => {
        if ((e.target as Element).closest('button, input')) return;
        // a double-click's second click is the stage's (Stage, QA SS-3)
        if (e.detail >= 2) return;
        // a pointer's first click waits for a second (detail 0: the keyboard's, at once)
        onTap({ wait: e.detail === 1 });
      }}
      // Enter or Space on the focused tile is its click
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        if (!e.repeat) onTap({ key: true });
      }}
      // right-click, or a long-press by touch (touch.ts sends contextmenu):
      // the stream's menu at the pointer, as Discord's
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        streamMenu({ x: e.clientX, y: e.clientY }, st, {
          focused,
          onFocus: () => onFocus(!focused),
          tile: stream || full ? e.currentTarget : null,
        });
      }}
    >
      {stream ? (
        <VideoView stream={stream} fit="contain" label={label} />
      ) : st.me || watching ? (
        <>
          <span className="hubv-tile-av">
            <Avatar user={user} name={name} size={av} />
          </span>
          {!st.me ? (
            <span className="hubv-tile-wait" role="status">
              <i className="nx-spin" aria-hidden="true" />
              {t('stream.loading')}
            </span>
          ) : null}
        </>
      ) : null}
      <span className="hubv-tile-live">
        <span className="nx-live-badge" role="img" aria-label={t('stream.liveLabel')}>
          {t('stream.live')}
        </span>
        <ViewerChip pid={st.pid} still={compact} />
        {/* its sound mark, unless its sound button is always shown (one sound control: QA V-15) */}
        {st.audio && !(soundButton && coarse) ? (
          <span
            className="hubv-tile-snd"
            role="img"
            aria-label={t(soundMuted ? 'stream.soundMuted' : 'stream.sound')}
            data-tip={t(soundMuted ? 'stream.soundMuted' : 'stream.sound')}
            data-muted={soundMuted || undefined}
          >
            <Icon name={soundMuted ? 'volume-x' : 'volume'} size="s" />
          </span>
        ) : !st.audio && !st.me && watching ? (
          // shared without sound: say so, or the missing volume looks like a
          // missing feature (owner, 2026-11-02)
          <button
            type="button"
            className="hubv-tile-snd"
            aria-label={t('stream.noSound')}
            data-tip={t('stream.noSound')}
            data-muted=""
            data-stream-nosound=""
            onClick={(e) =>
              openNote(e.currentTarget, t('stream.noSound'), t('stream.noSoundBody', { name }))
            }
          >
            <Icon name="volume-x" size="s" />
          </button>
        ) : null}
        {st.me && !sending ? <span className="hubv-tile-paused">{t('stream.paused')}</span> : null}
      </span>
      <span className="hubv-tile-tools hubv-stools">
        {/* a watched stream's sound: a click mutes it (Discord's), a mouse's
            hover shows its volume; a tap on a touch screen, with no hover,
            opens the volume and the mute */}
        {soundButton ? (
          <StreamSoundButton
            userId={st.userId}
            data={{ 'data-stream-sound': sound.muted ? 'muted' : 'on' }}
          />
        ) : null}
        {/* full screen keeps its way out while the video reloads (QA FS-1) */}
        {stream || full ? (
          <button
            type="button"
            className="hubv-tt"
            aria-label={t(full ? 'stream.exitFullscreen' : 'stream.fullscreen')}
            data-tip={t(full ? 'stream.exitFullscreen' : 'stream.fullscreen')}
            onClick={(e) => {
              const tile = e.currentTarget.closest<HTMLElement>('.hubv-tile');
              if (tile) toggleFullscreen(tile);
            }}
          >
            <Icon name="win-max" size="m" />
          </button>
        ) : null}
        <button
          type="button"
          className="hubv-tt"
          aria-haspopup="menu"
          aria-label={t('hubv.vr.more')}
          data-tip={t('hubv.vr.more')}
          onClick={(e) =>
            streamMenu(e.currentTarget, st, {
              focused,
              onFocus: () => onFocus(!focused),
            })
          }
        >
          <Icon name="more" size="m" />
        </button>
        {/* Stop watching (Discord's): on every watched stream, in words on the
            focused one; no more of its video or sound comes */}
        {!st.me && watching && !compact ? (
          <button
            type="button"
            className={'hubv-tt hubv-unwatch' + (unwatchWords ? ' lbl' : '')}
            aria-label={t('stream.stopWatching')}
            data-tip={unwatchWords ? undefined : t('stream.stopWatching')}
            data-stop-watching={st.userId}
            onClick={unwatch}
          >
            <Icon name="cm-eye-off" size="m" />
            {unwatchWords ? <span>{t('stream.stopWatching')}</span> : null}
          </button>
        ) : null}
      </span>
      {/* the name plate, and Stop streaming at its end: one row, so the
          plate never lies over the button (QA SS-2) */}
      <span className="hubv-tile-foot">
        <span className="hubv-tile-name">
          <Icon name="monitor" size="s" />
          <span dir="auto">{name}</span>
        </span>
        {st.me ? (
          <span className="hubv-tile-stop">
            <Btn
              kind="danger"
              size="xs"
              icon="x"
              data-stop-stream=""
              onClick={() => stopMedia('screen')}
            >
              {t('stream.stop')}
            </Btn>
          </span>
        ) : null}
      </span>
      {!stream && !st.me && !watching ? (
        // nothing is received until you click Watch (no preview: traffic);
        // last, so it lies over the name plate where a short tile makes
        // them meet, and a tap on Watch is Watch's (QA PH-1)
        // (area wrappers, not the kit's own classes, carry the strip's hiding and the lift)
        <span className="hubv-tile-gate">
          <span className="hubv-gate-av">
            <Avatar user={user} name={name} size={av} />
          </span>
          <span className="hubv-gate-go">
            <Btn
              kind="secondary"
              size="sm"
              icon="eye"
              className="hubv-watch"
              data-watch={st.userId}
              onClick={() => {
                onFocus(true);
                void watchStream(st.pid, true);
              }}
            >
              {t('stream.watch')}
            </Btn>
          </span>
        </span>
      ) : null}
    </div>
  );
}

/** The camera stream of a person's tile: your own preview, or what a video slot carries. */
export function useCameraStream(userId: Snowflake, me: boolean): MediaStream | null {
  const mine = useLocalMedia((m) => (me ? m.camera : null));
  const pid = useVoice((v) => {
    if (me) return null;
    for (const m of Object.values(v.session.members))
      if (m.user_id === userId && m.camera && m.channel === v.session.channelId) return m.pid;
    return null;
  });
  const carried = useVoice(
    (v) =>
      pid !== null &&
      Object.values(v.session.vslots).some((r) => r.pid === pid && r.source === 'camera'),
  );
  if (me) return mine;
  return pid !== null && carried ? remoteStream(pid, 'camera') : null;
}

/** The pid whose camera a person's tile would show (for `visible`), or null. */
export function cameraPidOf(
  members: Record<Pid, RosterMember>,
  userId: Snowflake,
  channelId: Snowflake,
): Pid | null {
  for (const m of Object.values(members))
    if (m.user_id === userId && m.camera && m.channel === channelId) return m.pid;
  return null;
}
